import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { X, Download, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const DISPOSITIONS = ["KEEP", "MODIFY", "REMOVE", "RESEARCH", "TBC"];
const PRIORITIES = ["Critical", "High", "Medium", "Later"];
const REVIEWERS = ["Keenan", "Kevin", "Jamiel", "Other"];

export function ValidationPanel({ screen, onClose }) {
  const { roleCode, persona } = useApp();
  const [form, setForm] = useState({
    feature: "", workflow: "", disposition: "KEEP", priority: "High",
    severity: "medium", comment: "", recommendation: "", missing: "", confusing: "",
    correct: null, realistic: null, useful: null, reviewer_name: "Keenan",
  });
  const [items, setItems] = useState([]);

  const load = () => api.feedback().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.feature) return toast.error("Enter the feature or element being reviewed");
    await api.createFeedback({ ...form, reviewer: form.reviewer_name, screen, role_code: roleCode });
    toast.success("Validation feedback recorded");
    setForm((f) => ({ ...f, feature: "", comment: "", recommendation: "", missing: "", confusing: "" }));
    load();
  };

  const Toggle3 = ({ label, k }) => (
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-600">{label}</span>
      <div className="flex gap-1">
        {[["Yes", true], ["No", false]].map(([t, v]) => (
          <button key={t} data-testid={`vf-${k}-${t.toLowerCase()}`}
            onClick={() => set(k, form[k] === v ? null : v)}
            className={cn("rounded border px-2 py-0.5 text-[11px]",
              form[k] === v ? "border-teal-500 bg-teal-50 text-teal-700" : "border-slate-300 text-slate-500")}>
            {t}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <aside data-testid="validation-panel"
      className="no-print flex w-96 shrink-0 flex-col border-l border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-900 px-4 py-3 text-white">
        <div>
          <div className="font-display text-sm font-bold">Validation Mode</div>
          <div className="text-[10px] text-slate-400">Capture feedback during the demo</div>
        </div>
        <button onClick={onClose} data-testid="validation-close" className="rounded p-1 hover:bg-slate-800">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        <div className="rounded-md bg-slate-50 p-2 text-[11px] text-slate-500">
          Reviewing: <span className="font-mono">{screen}</span> · as {persona?.name}
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Reviewer</Label>
          <div className="flex gap-1">
            {REVIEWERS.map((rv) => (
              <button key={rv} data-testid={`vf-reviewer-${rv.toLowerCase()}`} onClick={() => set("reviewer_name", rv)}
                className={cn("rounded border px-2 py-0.5 text-[11px]",
                  form.reviewer_name === rv ? "border-teal-500 bg-teal-50 text-teal-700" : "border-slate-300 text-slate-500")}>
                {rv}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Feature / element</Label>
          <Input data-testid="vf-feature" value={form.feature}
            onChange={(e) => set("feature", e.target.value)} placeholder="e.g. Evidence Assurance Graph" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Workflow</Label>
          <Input data-testid="vf-workflow" value={form.workflow}
            onChange={(e) => set("workflow", e.target.value)} placeholder="e.g. Readiness evaluation" />
        </div>

        <div className="space-y-2 rounded-md border border-slate-200 p-2.5">
          <Toggle3 label="Is it correct?" k="correct" />
          <Toggle3 label="Is it realistic?" k="realistic" />
          <Toggle3 label="Is it useful?" k="useful" />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Disposition</Label>
          <div className="grid grid-cols-5 gap-1">
            {DISPOSITIONS.map((d) => (
              <button key={d} data-testid={`vf-disposition-${d.toLowerCase()}`}
                onClick={() => set("disposition", d)}
                className={cn("rounded border px-1 py-1 text-[10px] font-semibold",
                  form.disposition === d ? "border-teal-500 bg-teal-50 text-teal-700" : "border-slate-300 text-slate-500")}>
                {d}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Priority</Label>
          <div className="flex gap-1">
            {PRIORITIES.map((p) => (
              <button key={p} data-testid={`vf-priority-${p}`} onClick={() => set("priority", p)}
                className={cn("rounded border px-3 py-0.5 text-[11px] font-semibold",
                  form.priority === p ? "border-amber-500 bg-amber-50 text-amber-700" : "border-slate-300 text-slate-500")}>
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">What is missing?</Label>
          <Textarea data-testid="vf-missing" rows={2} value={form.missing}
            onChange={(e) => set("missing", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Comment</Label>
          <Textarea data-testid="vf-comment" rows={2} value={form.comment}
            onChange={(e) => set("comment", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Recommendation</Label>
          <Textarea data-testid="vf-recommendation" rows={2} value={form.recommendation}
            onChange={(e) => set("recommendation", e.target.value)} />
        </div>

        <Button className="w-full bg-teal-600 hover:bg-teal-700" data-testid="vf-submit" onClick={submit}>
          Record Feedback
        </Button>

        <div className="pt-2">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-600">Recent feedback ({items.length})</span>
            <a href={api.exportFeedbackUrl()} data-testid="vf-export"
              className="inline-flex items-center gap-1 text-xs text-teal-700 hover:underline">
              <Download className="h-3 w-3" /> CSV
            </a>
          </div>
          <div className="space-y-1.5">
            {items.slice(0, 8).map((it) => (
              <div key={it.id} className="rounded-md border border-slate-200 p-2 text-[11px]">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-700">{it.feature || "—"}</span>
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-600">{it.disposition}</span>
                </div>
                <div className="text-slate-500">{it.screen} · {it.priority} · {it.reviewer}</div>
                {it.comment && <div className="mt-0.5 text-slate-600">{it.comment}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
}

export default ValidationPanel;
